import { toast } from "sonner";
import { api, getCurrentUser, isNetworkError } from "@/lib/api";
import { fiscalErrorMessage, sendFiscalReceipt } from "@/lib/fiscal";
import { enqueueSale, newOfflineId, pendingClosureCount, pendingCount, readClosureQueue, readQueue, removeClosure, removeSale, saleTotal } from "@/lib/offlineQueue";

/** Chiamate di chiusura: niente toast «server non raggiungibile», poi si stampa in negozio. */
export const SERVER_ATTEMPT = { skipNetworkToast: true, timeout: 12000 };

let flushing = false;
let started = false;

function salePayload(item) {
  return {
    offline_id: item.offline_id,
    mode: item.mode || "negozio",
    lines: item.lines || [],
    discount_pct: item.discount_pct || 0,
    discount_eur: item.discount_eur || 0,
    payment_method: item.payment_method || "cash",
    payment_breakdown: item.payment_breakdown || null,
    customer_id: item.customer_id || null,
    notes: item.notes || null,
    operator_name: item.operator_name || null,
    created_at: item.created_at || null,
    closed_at: item.created_at || null,
    rt_info: item.rt_info || null,
    cash_tendered: item.cash_tendered ?? null,
    lottery_code: item.lottery_code || null,
    table_id: item.table_id || null,
    store_id: item.store_id || null,
    delivery: item.delivery || null,
    source: "cassa",
  };
}

function alreadyClosed(err) {
  const detail = String(err?.response?.data?.detail || "");
  return err?.response?.status === 400 && /chiuso/i.test(detail);
}

async function pushOne(item) {
  const quiet = { skipNetworkToast: true, timeout: 20000 };
  if (item.server_order_id) {
    try {
      await api.patch(`/orders/${item.server_order_id}`, {
        lines: item.lines || [],
        discount_pct: item.discount_pct || 0,
        discount_eur: item.discount_eur || 0,
        notes: item.notes || null,
        lottery_code: item.lottery_code || null,
      }, quiet);
    } catch (err) {
      if (alreadyClosed(err)) {
        if (item.rt_info) await api.post(`/orders/${item.server_order_id}/rt-info`, item.rt_info, quiet).catch(() => {});
        return "done";
      }
      if (err?.response?.status !== 404) throw err;
    }
    try {
      const c = await api.post(`/orders/${item.server_order_id}/close-fiscal`, item.close_body || {
        payment_method: item.payment_method || "cash",
      }, quiet);
      const oid = c.data?.id || item.server_order_id;
      if (item.rt_info) await api.post(`/orders/${oid}/rt-info`, item.rt_info, quiet).catch(() => {});
      return "done";
    } catch (err) {
      if (alreadyClosed(err)) return "done";
      if (err?.response?.status !== 404) throw err;
    }
  }
  const r = await api.post("/orders/offline-sync", [salePayload(item)], quiet);
  const st = r.data?.results?.[0]?.status;
  if (st === "created" || st === "duplicate" || st === "closed") return "done";
  return "keep";
}

async function pushClosure(item) {
  const q = new URLSearchParams({ kind: item.kind || "x", offline_id: item.offline_id });
  if (item.created_at) q.set("at", item.created_at);
  await api.post(`/closures?${q.toString()}`, null, { skipNetworkToast: true, timeout: 20000 });
}

/** Invia le vendite già stampate, poi le chiusure X/Z. Non ristampa. */
export async function flushOfflineSales() {
  if (flushing) return { synced: 0 };
  const items = readQueue();
  const closures = readClosureQueue();
  if (!items.length && !closures.length) return { synced: 0 };
  flushing = true;
  let synced = 0;
  let closuresSynced = 0;
  let salesBlocked = false;
  try {
    for (const item of items) {
      try {
        const result = await pushOne(item);
        if (result === "done") {
          removeSale(item.offline_id);
          synced += 1;
        }
      } catch {
        salesBlocked = true;
        break;
      }
    }
    if (!salesBlocked) {
      for (const item of closures) {
        try {
          await pushClosure(item);
          removeClosure(item.offline_id);
          closuresSynced += 1;
        } catch {
          break;
        }
      }
    }
  } finally {
    flushing = false;
  }
  if (synced > 0) {
    const left = pendingCount();
    toast.success(left
      ? `${synced} scontrini registrati. Ne restano ${left} in attesa.`
      : `${synced} scontrin${synced === 1 ? "o registrato" : "i registrati"} sul server.`);
  }
  if (closuresSynced > 0) {
    toast.success(closuresSynced === 1
      ? "Chiusura registrata sul server."
      : `${closuresSynced} chiusure registrate sul server.`);
  }
  return { synced, closuresSynced };
}

export function startOfflineSync() {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("online", () => { flushOfflineSales(); });
  window.setInterval(() => {
    if (pendingCount() || pendingClosureCount()) flushOfflineSales();
  }, 15000);
  flushOfflineSales();
}

/**
 * Il server non ha risposto. Stampa sulla RT del negozio e ricorda la vendita.
 * Se la stampante non conferma, il conto resta in cassa e non si mette in coda.
 */
export async function recoverUnreachableSale(err, sale) {
  if (!(isNetworkError(err) || err?.isNetworkError)) return { handled: false, printed: false };
  if (Number(sale?.redeem_points) > 0) {
    toast.error("Lo sconto punti ha bisogno del server. Chiudi senza punti, oppure aspetta la linea.");
    return { handled: true, printed: false };
  }
  const lines = Array.isArray(sale?.lines) ? sale.lines : [];
  if (!lines.length) {
    toast.error("Server non raggiungibile e nessun articolo da stampare.");
    return { handled: true, printed: false };
  }
  const total = saleTotal(lines, sale.discount_pct, sale.discount_eur);
  const order = {
    lines,
    total,
    discount_pct: sale.discount_pct || 0,
    discount_eur: sale.discount_eur || 0,
    payment_method: sale.payment_method || "cash",
    payment_breakdown: sale.payment_breakdown || null,
  };
  const res = await sendFiscalReceipt({ tenant: sale.tenant, order });
  if (!res?.ok) {
    toast.error(`${fiscalErrorMessage(res)}. Lo scontrino non è uscito: il conto resta in cassa.`, { duration: 12000 });
    return { handled: true, printed: false };
  }
  const offline_id = sale.offline_id || newOfflineId();
  enqueueSale({
    offline_id,
    server_order_id: sale.server_order_id || null,
    rt_info: res.rt || null,
    printed: true,
    created_at: new Date().toISOString(),
    mode: sale.mode || "negozio",
    lines,
    discount_pct: sale.discount_pct || 0,
    discount_eur: sale.discount_eur || 0,
    payment_method: sale.payment_method || "cash",
    payment_breakdown: sale.payment_breakdown || null,
    customer_id: sale.customer_id || null,
    notes: sale.notes || null,
    operator_name: sale.operator_name || getCurrentUser()?.name || null,
    cash_tendered: sale.cash_tendered ?? null,
    lottery_code: sale.lottery_code || null,
    table_id: sale.table_id || null,
    store_id: sale.store_id || null,
    delivery: sale.delivery || null,
    close_body: {
      payment_method: sale.payment_method || "cash",
      payment_breakdown: sale.payment_breakdown || null,
      discount_pct: sale.discount_pct || 0,
      discount_eur: sale.discount_eur || 0,
      cash_tendered: sale.cash_tendered ?? null,
      lottery_code: sale.lottery_code || null,
    },
  });
  toast.success("Scontrino emesso sulla stampante. Si registra sul server quando torna la linea.");
  flushOfflineSales();
  return { handled: true, printed: true, order: { ...order, id: offline_id, receipt_type: "fiscal" } };
}
